import { useEffect, useId, useRef, useState, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { DiffEditor, type DiffEditorProps } from '@monaco-editor/react';
import { linguagemDe } from '../editor/monaco';
import { usarTemaDoEditor } from '../estado/tema';
import { usarSessao } from '../estado/sessao';
import { Modal } from '../componentes/Modal';
import { IconeAbrir, IconeFechado } from '../componentes/icones';

/**
 * Peças de tela do Toolkit (F3.7) que mais de uma aba usa: o campo de texto
 * longo, o diálogo que pede motivo, o bloco de comparação e o aviso de erro.
 *
 * Nada de controle nativo (`web-controles.md`): escolha é `CampoDeEscolha`,
 * marcar é `CaixaDeMarcar`, perguntar é `Modal`/`usarConfirmacao`. O
 * `<textarea>` não está na lista — é texto, e o navegador não o pinta com a
 * cara do sistema operacional.
 */

/** Decidir, marcar, criar e arquivar são de OPERADOR+ (D20). A API recusa de novo. */
export function usarPodeComandar(): boolean {
  const papel = usarSessao((e) => e.usuario?.papel);
  return papel === 'ADMIN' || papel === 'OPERADOR';
}

const CLASSE_DO_CAMPO =
  'w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none transition placeholder:text-zinc-600 focus:border-zinc-500 pointer-coarse:text-base';

/*
  A dica fica FORA do `<label>` e entra por `aria-describedby`: dentro dele ela
  viraria parte do NOME do campo, e o leitor de tela anunciaria "Nome
  minúsculas, dígitos e hífen…" a cada visita.
*/

/** Texto longo com rótulo: motivo, semelhança, descrição, texto do verbete. */
export function CampoDeTextoLongo({
  rotulo,
  dica,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { rotulo: string; dica?: string }): React.JSX.Element {
  const idDaDica = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <label className="block space-y-1.5">
        <span className="text-sm text-zinc-400">{rotulo}</span>
        <textarea
          {...props}
          {...(dica ? { 'aria-describedby': idDaDica } : {})}
          className={CLASSE_DO_CAMPO + ' min-h-20 resize-y'}
        />
      </label>
      {dica ? (
        <span id={idDaDica} className="block text-xs text-zinc-500">
          {dica}
        </span>
      ) : null}
    </div>
  );
}

/** Uma linha de texto com rótulo — o `Campo` da casa com o tamanho das outras peças daqui. */
export function CampoDeLinha({
  rotulo,
  dica,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { rotulo: string; dica?: string }): React.JSX.Element {
  const idDaDica = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <label className="block space-y-1.5">
        <span className="text-sm text-zinc-400">{rotulo}</span>
        <input
          {...props}
          {...(dica ? { 'aria-describedby': idDaDica } : {})}
          type={props.type ?? 'text'}
          className={CLASSE_DO_CAMPO}
        />
      </label>
      {dica ? (
        <span id={idDaDica} className="block text-xs text-zinc-500">
          {dica}
        </span>
      ) : null}
    </div>
  );
}

export function AvisoDeErro({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <p role="alert" className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200">
      {children}
    </p>
  );
}

const BOTAO_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-40 pointer-coarse:min-h-11';

export function BotaoPrimario(props: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'button'}
      className={BOTAO_BASE + ' bg-sky-600 font-medium text-white hover:bg-sky-500 ' + (props.className ?? '')}
    />
  );
}

export function BotaoPerigoso(props: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'button'}
      className={BOTAO_BASE + ' bg-red-700 font-medium text-white hover:bg-red-600 ' + (props.className ?? '')}
    />
  );
}

export function BotaoSecundario(props: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return (
    <button
      {...props}
      type={props.type ?? 'button'}
      className={BOTAO_BASE + ' border border-zinc-700 text-zinc-200 hover:bg-zinc-800 ' + (props.className ?? '')}
    />
  );
}

/**
 * Um diálogo que só aceita com MOTIVO: recusar uma proposta, marcar e
 * desmarcar `quebrada`. O motivo é o que fica no histórico append-only ao
 * lado da decisão, e uma decisão sem ele é a regra velha convivendo com a
 * nova sem ninguém saber por quê (o que a pré-fase mediu na JusAPI).
 */
export function DialogoDeMotivo({
  titulo,
  aberto,
  explicacao,
  rotuloDoBotao,
  perigoso = false,
  ocupado = false,
  erro,
  aoConfirmar,
  aoFechar,
}: {
  titulo: string;
  aberto: boolean;
  explicacao: ReactNode;
  rotuloDoBotao: string;
  perigoso?: boolean;
  ocupado?: boolean;
  erro?: string | null;
  aoConfirmar: (motivo: string) => void;
  aoFechar: () => void;
}): React.JSX.Element | null {
  const [motivo, setMotivo] = useState('');
  // Cada abertura começa limpa: o motivo de uma recusa não pode aparecer
  // escrito na próxima.
  useEffect(() => {
    if (aberto) setMotivo('');
  }, [aberto]);
  const Botao = perigoso ? BotaoPerigoso : BotaoPrimario;
  return (
    <Modal
      titulo={titulo}
      aberto={aberto}
      aoFechar={aoFechar}
      rodape={
        <>
          <BotaoSecundario onClick={aoFechar}>Cancelar</BotaoSecundario>
          <Botao disabled={ocupado || motivo.trim().length === 0} onClick={() => aoConfirmar(motivo.trim())}>
            {rotuloDoBotao}
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        <div className="text-sm text-zinc-300">{explicacao}</div>
        <CampoDeTextoLongo
          rotulo="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Por que — fica no histórico, ao lado da decisão"
          maxLength={2_000}
        />
        {erro ? <AvisoDeErro>{erro}</AvisoDeErro> : null}
      </div>
    </Modal>
  );
}

const ROTULO_DA_SITUACAO: Record<'novo' | 'removido' | 'alterado' | 'igual', string> = {
  novo: 'novo',
  removido: 'removido',
  alterado: 'alterado',
  igual: 'sem mudança',
};

const COR_DA_SITUACAO: Record<'novo' | 'removido' | 'alterado' | 'igual', string> = {
  novo: 'text-emerald-300',
  removido: 'text-red-300',
  alterado: 'text-amber-300',
  igual: 'text-zinc-500',
};

/**
 * O `DiffEditor` que SAI DA TELA sem derrubar a página.
 *
 * Medido no ensaio de tela: ao decidir uma proposta, o cartão some, e o
 * `@monaco-editor/react` 4.7 desmonta o editor descartando os MODELOS antes do
 * próprio editor — o Monaco lança "TextModel got disposed before
 * DiffEditorWidget model got reset", e a exceção sobe solta na página. O
 * conserto é inverter a ordem: o componente guarda os modelos
 * (`keepCurrent…Model`), o editor é descartado primeiro, e os modelos vão
 * logo depois, fora do ciclo de desmontagem — sem vazar um modelo por diff
 * aberto.
 */
function DiffQueSaiLimpo(props: DiffEditorProps): React.JSX.Element {
  const modelos = useRef<{ isDisposed(): boolean; dispose(): void }[]>([]);
  useEffect(
    () => () => {
      const soltos = modelos.current;
      setTimeout(() => {
        for (const m of soltos) if (!m.isDisposed()) m.dispose();
      }, 0);
    },
    [],
  );
  return (
    <DiffEditor
      {...props}
      keepCurrentOriginalModel
      keepCurrentModifiedModel
      onMount={(editor, monaco) => {
        const par = editor.getModel();
        modelos.current = par ? [par.original, par.modified] : [];
        props.onMount?.(editor, monaco);
      }}
    />
  );
}

/**
 * Um bloco de comparação: o nome, a situação e — aberto — o `DiffEditor` do
 * Monaco, o mesmo do editor e do cartão de edição da conversa (servido pelo
 * próprio domínio, CSP).
 *
 * Em linha, nunca lado a lado: numa tela estreita duas colunas viram duas
 * colunas ilegíveis. A altura vem do que o editor desenhou, com teto — o
 * mesmo ajuste do `CartaoDeEdicao`.
 */
export function BlocoDeDiff({
  chave,
  titulo,
  situacao,
  antes,
  depois,
  linguagem,
  abertoDeInicio,
}: {
  /** Distingue os modelos do Monaco: sem ela, dois blocos com o mesmo nome dividiriam o texto. */
  chave: string;
  titulo: string;
  situacao: 'novo' | 'removido' | 'alterado' | 'igual';
  antes: string;
  depois: string;
  linguagem?: string;
  abertoDeInicio: boolean;
}): React.JSX.Element {
  const [aberto, setAberto] = useState(abertoDeInicio);
  const [altura, setAltura] = useState(160);
  const tema = usarTemaDoEditor();
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40" data-testid="bloco-de-diff" data-caminho={titulo}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-xs pointer-coarse:min-h-11"
      >
        {aberto ? <IconeAbrir tamanho={14} /> : <IconeFechado tamanho={14} />}
        <span className="min-w-0 flex-1 truncate font-mono text-zinc-200">{titulo}</span>
        <span className={'shrink-0 ' + COR_DA_SITUACAO[situacao]}>{ROTULO_DA_SITUACAO[situacao]}</span>
      </button>
      {aberto ? (
        <div className="border-t border-zinc-800" style={{ height: altura }}>
          <DiffQueSaiLimpo
            original={antes}
            modified={depois}
            originalModelPath={`toolkit-antes/${chave}/${titulo}`}
            modifiedModelPath={`toolkit-depois/${chave}/${titulo}`}
            language={linguagem ?? linguagemDe(titulo)}
            theme={tema}
            onMount={(editor) => {
              const modificado = editor.getModifiedEditor();
              const medir = (): void => setAltura(Math.min(420, Math.max(72, modificado.getContentHeight() + 12)));
              medir();
              modificado.onDidContentSizeChange(medir);
            }}
            options={{
              readOnly: true,
              renderSideBySide: false,
              lineNumbers: 'on',
              diffWordWrap: 'on',
              wordWrap: 'on',
              hideUnchangedRegions: { enabled: true, contextLineCount: 2, minimumLineCount: 4 },
              fontSize: 12,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              renderOverviewRuler: false,
              folding: false,
              scrollbar: { alwaysConsumeMouseWheel: false },
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/** As abas do Toolkit, com o papel de abas para quem lê por leitor de tela. */
export function Abas<T extends string>({
  abas,
  atual,
  aoTrocar,
}: {
  abas: { id: T; rotulo: string; contagem?: number }[];
  atual: T;
  aoTrocar: (id: T) => void;
}): React.JSX.Element {
  return (
    <div role="tablist" aria-label="Seções do Toolkit" className="flex flex-wrap gap-1 border-b border-zinc-800">
      {abas.map((aba) => (
        <button
          key={aba.id}
          type="button"
          role="tab"
          aria-selected={aba.id === atual}
          onClick={() => aoTrocar(aba.id)}
          className={
            '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition pointer-coarse:min-h-11 ' +
            (aba.id === atual
              ? 'border-sky-400 text-zinc-100'
              : 'border-transparent text-zinc-400 hover:text-zinc-200')
          }
        >
          {aba.rotulo}
          {aba.contagem ? (
            <span
              title="propostas esperando decisão"
              className="rounded-full bg-sky-500/20 px-1.5 text-[11px] tabular-nums text-sky-300"
            >
              {aba.contagem}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}
